// =====================================================================
// 假行 / JiaFlight — fetch 封装
// 自动带 JWT，处理 401 跳转登录
// 针对国内移动网络优化：超时 + 失败重试 + 友好错误提示
// =====================================================================

import { router } from '@/router';

const TOKEN_KEY = 'jiaflight_token';
const REFRESH_TOKEN_KEY = 'jiaflight_refresh_token';

/** API 基础地址：开发环境走 vite proxy（相对路径），生产环境从环境变量读取 */
const API_BASE = (import.meta.env.VITE_API_BASE as string) || '';

/** 获取本地存储的 access token */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** 设置 token（access + refresh） */
export function setToken(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/** 清除 token */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** API 统一响应格式 */
export interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/** 请求选项 */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  // 是否跳过自动添加 Authorization header
  skipAuth?: boolean;
  // 请求超时（毫秒），默认 15 秒（移动网络适配）
  timeout?: number;
  // 失败重试次数，默认 GET 请求 1 次，其他 0 次
  retries?: number;
}

/** 默认超时：15 秒（国内移动网络波动时给足时间） */
const DEFAULT_TIMEOUT = 15000;

/** 单次 fetch 调用（带超时控制） */
async function fetchWithTimeout(
  path: string,
  init: RequestInit,
  timeout: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(path, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** 基础请求函数 */
export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, skipAuth, headers, timeout = DEFAULT_TIMEOUT, retries, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  };

  if (!skipAuth) {
    const token = getToken();
    if (token) {
      finalHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const method = (rest.method as string) ?? 'GET';
  // GET 请求默认重试 1 次（网络波动），其他方法默认不重试（避免重复提交）
  const maxRetries = retries ?? (method === 'GET' ? 1 : 0);

  const init: RequestInit = {
    ...rest,
    method,
    headers: finalHeaders,
    body: body != null ? JSON.stringify(body) : undefined,
  };

  let lastError: Error = new Error('请求失败');
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const url = API_BASE + path;
      const resp = await fetchWithTimeout(url, init, timeout);

      // 401 自动跳转登录（不重试）
      if (resp.status === 401 && !skipAuth) {
        clearToken();
        setTimeout(() => {
          router.push({ name: 'login' });
        }, 0);
        throw new Error('登录已过期，请重新登录');
      }

      // 解析 JSON（可能为空）
      let json: ApiResult<T> | null = null;
      const text = await resp.text();
      if (text) {
        try {
          json = JSON.parse(text) as ApiResult<T>;
        } catch {
          if (!resp.ok) {
            throw new Error(`HTTP ${resp.status}: ${text}`);
          }
          return text as unknown as T;
        }
      }

      if (!resp.ok) {
        const msg = json?.error ?? json?.message ?? `HTTP ${resp.status}`;
        throw new Error(msg);
      }

      if (json && json.success) {
        return json.data as T;
      }

      const errMsg = json?.error ?? json?.message ?? '请求失败';
      throw new Error(errMsg);
    } catch (err) {
      lastError = err as Error;
      // 仅对网络错误/超时重试，不对业务错误重试
      const isNetworkError =
        err instanceof TypeError || // fetch 网络失败
        (err instanceof DOMException && err.name === 'AbortError'); // 超时
      if (!isNetworkError || attempt >= maxRetries) {
        break;
      }
      // 重试前等待 500ms（避免瞬间重试）
      await new Promise((r) => setTimeout(r, 500));
      attempt++;
    }
  }

  // 网络错误给用户友好的提示
  if (lastError instanceof DOMException && lastError.name === 'AbortError') {
    throw new Error('请求超时，请检查网络后重试');
  }
  if (lastError instanceof TypeError) {
    throw new Error('网络连接失败，请检查网络设置');
  }
  throw lastError;
}

/** 便捷方法 */
export const http = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
